import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  Check,
  FileText,
  Footprints,
  Scale,
  ShieldOff,
  Users,
} from 'lucide-react';
import PartnerBriefSheet from '../components/PartnerBriefSheet';

const PARTNER_MAIL =
  'mailto:partenaires@bookshare.be?subject=Pilote%20BookShare%20%E2%80%94%20biblioth%C3%A8que';

const gains = [
  {
    icon: Footprints,
    title: 'Du passage, pas du catalogue concurrent',
    text: 'Chaque remise est une visite. Des lecteurs qui n’ont souvent pas de carte communale. Vous restez le lieu ; BookShare n’emprunte pas votre fonds.',
  },
  {
    icon: Users,
    title: 'Un public que le réseau n’atteint plus',
    text: '25–40 ans, titres épuisés, langues, BD, universitaires. Ce que vous n’achèterez pas, ils se le prêtent — dans votre hall.',
  },
  {
    icon: Building2,
    title: 'De l’ordre, pas du street-meetup',
    text: 'Mieux un échange de 5 minutes sous caméras, aux heures d’ouverture, qu’un parking. Même logique qu’un espace citoyen : l’infra existe déjà.',
  },
  {
    icon: Scale,
    title: 'Zéro métier nouveau',
    text: 'Pas de stock BookShare, pas de médiation au guichet, pas d’encaissement. Le personnel n’arbitre rien. La caution reste chez nous.',
  },
];

const Partners: React.FC = () => {
  return (
    <div>
      <section className="border-b border-gray-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <p className="section-label mb-3">Bibliothèques · Bruxelles</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink leading-tight">
            Vous n’hébergez pas une app.
            <br />
            Vous restez le lieu de la lecture.
          </h1>
          <p className="mt-6 text-lg text-ink-muted leading-relaxed">
            BookShare fait circuler des livres de particuliers. La remise se fait chez vous, cinq
            minutes, hall ouvert. Pas d’argent, pas de rayon BookShare, pas de charge pour vos
            équipes. Un pilote de trois mois, une antenne, réversible.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={PARTNER_MAIL} className="btn btn-primary">
              Proposer une antenne
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
            <Link to="/partenaires/dossier" className="btn btn-outline">
              <FileText className="h-4 w-4 mr-2" />
              Dossier 1 page (PDF)
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <p className="section-label mb-3">Intérêt</p>
        <h2 className="font-serif text-3xl text-ink mb-10">Ce que la bibliothèque y gagne</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {gains.map(({ icon: Icon, title, text }) => (
            <article key={title} className="card p-6">
              <Icon className="h-5 w-5 text-forest-600 mb-4" />
              <h3 className="font-medium text-ink mb-2">{title}</h3>
              <p className="text-sm text-ink-muted leading-relaxed">{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 grid md:grid-cols-2 gap-12">
          <div>
            <p className="section-label mb-3">Protocole</p>
            <h2 className="font-serif text-3xl text-ink mb-4">Ce que nous ne demandons jamais</h2>
            <p className="text-ink-muted leading-relaxed mb-6">
              Dès que ça devient du travail de guichet, c’est non. Le pilote est conçu pour qu’un
              juriste communal et un directeur puissent signer sans créer de poste.
            </p>
            <ul className="space-y-3 text-sm text-ink-muted">
              {[
                'Pas de livres BookShare dans vos rayons',
                'Pas de scan, validation ou arbitrage par un agent',
                'Pas de commission ni d’espèces sur place',
                'Pas d’exclusivité, pas de totem publicitaire',
              ].map((item) => (
                <li key={item} className="flex gap-3">
                  <ShieldOff className="h-4 w-4 text-forest-600 shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="section-label mb-3">Offre</p>
            <h2 className="font-serif text-3xl text-ink mb-4">Pilote qui fait dire oui</h2>
            <ul className="space-y-3 text-sm">
              {[
                ['Durée', '3 mois, sortie à tout moment, sans frais'],
                ['Périmètre', 'Une seule implantation'],
                ['Lieu', 'Hall, heures d’ouverture uniquement'],
                ['Responsabilité', 'Utilisateurs et BookShare — pas le personnel'],
                ['Suivi', 'Chiffres de passage agrégés, chaque mois'],
              ].map(([k, v]) => (
                <li key={k} className="flex justify-between gap-4 border-b border-gray-100 py-2">
                  <span className="text-ink-muted">{k}</span>
                  <span className="text-ink text-right">{v}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-ink-muted leading-relaxed flex gap-2">
              <Check className="h-4 w-4 text-forest-600 shrink-0 mt-0.5" />
              Un rendez-vous de rue n’est pas couvert par la garantie BookShare. C’est aussi pour
              ça que le hall public a un sens.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <p className="section-label mb-3">En 30 secondes</p>
        <blockquote className="font-serif text-xl sm:text-2xl text-ink leading-snug">
          On n’est pas une bibliothèque privée. On ne touche pas à votre fonds. Les gens se
          remettent un livre chez vous. Vous gagnez des visites et un rôle de lieu sûr. Pas de
          charge, pas de stock, pas de litige.
        </blockquote>
        <p className="mt-8 text-ink-muted text-sm leading-relaxed">
          Pour un échevin ou un directeur : PDF d’une page à imprimer ou enregistrer, puis un mail.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/partenaires/dossier" className="btn btn-primary">
            Ouvrir le dossier A4
          </Link>
          <a href={PARTNER_MAIL} className="btn btn-outline">
            partenaires@bookshare.be
          </a>
        </div>
      </section>

      <section className="bg-paper-dark/60 border-t border-gray-200 py-16">
        <div className="max-w-3xl mx-auto px-4">
          <p className="section-label mb-4 text-center">Aperçu du dossier</p>
          <div className="hidden sm:block shadow-card border border-gray-200">
            <PartnerBriefSheet />
          </div>
          <p className="sm:hidden text-center text-sm text-ink-muted">
            <Link to="/partenaires/dossier" className="text-forest-700 underline underline-offset-2">
              Voir le dossier sur une page
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Partners;
